import { FC, ReactNode } from 'react'
import { DragDropContext, Draggable, DroppableProvided, DropResult } from 'react-beautiful-dnd'
import {
  Box, Button, Chip, CircularProgress, Skeleton, Typography,
} from '@mui/material'
import { HCPFlowEnum, IHCPFlowKanbanColumn } from '../../../Services/HCP/flows/hcp-flow.interface'
import { getSheetColumnTiragem } from '../../../Services/HCP/flows/hcp-flow-sheet-columns'
import { canDragHCPFlow, IFlowCardMovePayload } from '../../../Services/HCP/flows/hcp-flow-drag.helpers'
import { getColumnLoadMoreLabel } from '../../../Services/HCP/flows/hcp-flow-pagination.helpers'
import { IFlowManagementCardSelection, IHCPFlowKanbanCard } from '../../../Services/HCP/flows/hcp-flow-card.interface'
import { StrictModeDroppable } from '../../KanbanBoardProduction/components/droppable-component'
import FlowManagementCard from './flow-management-card.component'

const COLUMN_WIDTH = 260
const COLUMN_GAP = 1.5

const COLUMN_PALETTE = [
  { bg: '#e3f2fd', color: '#1565c0' },
  { bg: '#fff8e1', color: '#f57f17' },
  { bg: '#f3e5f5', color: '#6a1b9a' },
  { bg: '#fce4ec', color: '#c62828' },
  { bg: '#e8f5e9', color: '#2e7d32' },
  { bg: '#e0f2f1', color: '#00695c' },
  { bg: '#fff3e0', color: '#e65100' },
  { bg: '#ede7f6', color: '#4527a0' },
  { bg: '#f1f8e9', color: '#558b2f' },
]

const columnSx = {
  minWidth: COLUMN_WIDTH,
  maxWidth: COLUMN_WIDTH,
  flexShrink: 0,
  height: '100%',
}

function getColumnStyle(index: number) {
  return COLUMN_PALETTE[index % COLUMN_PALETTE.length]
}

interface IFlowManagementKanbanProps {
  columns: IHCPFlowKanbanColumn[]
  activeFlow?: HCPFlowEnum
  loading?: boolean
  loadingMoreColumnId?: string | null
  movingCardId?: string | null
  onLoadMoreColumn?: (column: IHCPFlowKanbanColumn) => void
  onCardClick?: (selection: IFlowManagementCardSelection) => void
  onCardMove?: (payload: IFlowCardMovePayload) => void
}

function ColumnHeader({
  column,
  color,
  showTiragem,
}: {
  column: IHCPFlowKanbanColumn
  color: string
  showTiragem: boolean
}) {
  const tiragem = showTiragem ? getSheetColumnTiragem(column) : null
  const podeFechar = tiragem && tiragem.minimo > 0 && tiragem.total >= tiragem.minimo

  return (
    <Box sx={{ flexShrink: 0, px: 0.5, py: 0.75, minHeight: 36 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ color }} noWrap title={column.name}>
          {column.name}
        </Typography>
        <Chip
          label={column.total}
          size="small"
          sx={{
            ml: 1,
            height: 20,
            fontSize: '0.70rem',
            fontWeight: 700,
            bgcolor: color,
            color: '#fff',
          }}
        />
      </Box>

      {tiragem && tiragem.minimo > 0 && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mt: 0.25,
            color: podeFechar ? '#2e7d32' : 'text.secondary',
            fontWeight: podeFechar ? 700 : 500,
            fontSize: '0.65rem',
          }}
        >
          {tiragem.total.toLocaleString('pt-BR')} / {tiragem.minimo.toLocaleString('pt-BR')} un
          {podeFechar ? ' · pode fechar' : ''}
        </Typography>
      )}
    </Box>
  )
}

interface IColumnBodyProps {
  column: IHCPFlowKanbanColumn
  palette: { bg: string; color: string }
  activeFlow?: HCPFlowEnum
  canDrag: boolean
  isDraggingOver?: boolean
  isMoving: boolean
  loadingMoreColumnId?: string | null
  movingCardId?: string | null
  droppableProvided?: DroppableProvided
  onLoadMoreColumn?: (column: IHCPFlowKanbanColumn) => void
  onCardClick?: (selection: IFlowManagementCardSelection) => void
}

function ColumnBody({
  column,
  palette,
  activeFlow,
  canDrag,
  isDraggingOver = false,
  isMoving,
  loadingMoreColumnId,
  movingCardId,
  droppableProvided,
  onLoadMoreColumn,
  onCardClick,
}: IColumnBodyProps) {
  const isLoadingMore = loadingMoreColumnId === column.statusId

  return (
    <Box
      ref={droppableProvided?.innerRef}
      {...(droppableProvided?.droppableProps ?? {})}
      sx={{
        flex: 1,
        minHeight: 0,
        height: '100%',
        background: isDraggingOver ? '#fff' : palette.bg,
        borderRadius: 2,
        padding: 1.5,
        overflowY: 'auto',
        overflowX: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        outline: isDraggingOver ? '2px dashed' : 'none',
        outlineColor: palette.color,
        transition: 'background 0.15s ease',
        '&::-webkit-scrollbar': { width: 4 },
        '&::-webkit-scrollbar-track': { background: 'transparent' },
        '&::-webkit-scrollbar-thumb': { background: '#ccc', borderRadius: 2 },
      }}
    >
      {column.cards.length === 0 && !isDraggingOver && (
        <Typography variant="caption" color="text.secondary" sx={{ px: 0.5, py: 2, textAlign: 'center' }}>
          {canDrag ? 'Arraste cards para esta coluna.' : 'Nenhum card nesta coluna.'}
        </Typography>
      )}

      {column.cards.map((card, cardIndex) => {
        const cardId = String(card.id)
        const cardIsMoving = movingCardId === cardId

        const handleClick = onCardClick
          ? (selectedCard: IHCPFlowKanbanCard) => onCardClick({
            card: selectedCard,
            columnName: column.name,
            columnSequence: column.sequence,
            columnDisplayCode: column.displayCode,
          })
          : undefined

        if (!canDrag) {
          return (
            <FlowManagementCard
              key={cardId}
              card={card}
              flow={activeFlow}
              onClick={handleClick}
            />
          )
        }

        return (
          <Draggable
            key={cardId}
            draggableId={cardId}
            index={cardIndex}
            isDragDisabled={isMoving}
          >
            {(dragProvided, dragSnapshot) => (
              <Box
                ref={dragProvided.innerRef}
                {...dragProvided.draggableProps}
                {...dragProvided.dragHandleProps}
                sx={{
                  opacity: cardIsMoving ? 0.55 : dragSnapshot.isDragging ? 0.9 : 1,
                  cursor: isMoving ? 'wait' : 'grab',
                }}
              >
                <FlowManagementCard
                  card={card}
                  flow={activeFlow}
                  isMoving={cardIsMoving}
                  onClick={handleClick}
                />
              </Box>
            )}
          </Draggable>
        )
      })}

      {droppableProvided?.placeholder}

      {column.hasNextPage && onLoadMoreColumn && (
        <Button
          size="small"
          variant="outlined"
          disabled={isLoadingMore || isMoving}
          onClick={() => onLoadMoreColumn(column)}
          sx={{ mt: 0.5, alignSelf: 'center', flexShrink: 0 }}
        >
          {isLoadingMore ? <CircularProgress size={18} /> : getColumnLoadMoreLabel(column)}
        </Button>
      )}
    </Box>
  )
}

const FlowManagementKanban: FC<IFlowManagementKanbanProps> = ({
  columns,
  activeFlow,
  loading,
  loadingMoreColumnId,
  movingCardId,
  onLoadMoreColumn,
  onCardClick,
  onCardMove,
}) => {
  const isSheetFlow = activeFlow === HCPFlowEnum.SHEET
  const canDrag = canDragHCPFlow(activeFlow) && Boolean(onCardMove)
  const isMoving = Boolean(movingCardId)

  const handleDragEnd = (result: DropResult) => {
    if (!onCardMove || !canDrag || isMoving) return

    const { destination, source, draggableId } = result
    if (!destination) return
    if (destination.droppableId === source.droppableId) return

    const sourceColumn = columns.find((column) => column.statusId === source.droppableId)
    const destColumn = columns.find((column) => column.statusId === destination.droppableId)
    if (!sourceColumn || !destColumn) return

    const card = sourceColumn.cards.find((item) => String(item.id) === draggableId)
    if (!card) return

    onCardMove({
      card,
      sourceStatusId: source.droppableId,
      destStatusId: destination.droppableId,
      destIndex: destination.index,
      destSequence: destColumn.sequence,
    })
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', gap: COLUMN_GAP, overflowX: 'auto', overflowY: 'hidden', alignItems: 'stretch', height: '100%', pb: 1 }}>
        {Array.from({ length: 5 }).map((_, i) => (
          <Box key={i} sx={{ ...columnSx, display: 'flex', flexDirection: 'column' }}>
            <Skeleton variant="text" width={160} height={28} sx={{ mb: 1 }} />
            <Box sx={{ flex: 1, bgcolor: '#f0f0f0', borderRadius: 2, p: 1.5 }}>
              {Array.from({ length: 3 }).map((__, j) => (
                <Skeleton key={j} variant="rounded" height={110} sx={{ mb: 1, borderRadius: 2 }} />
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    )
  }

  if (!columns.length) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          {activeFlow === HCPFlowEnum.LOGISTICS
            ? 'Nenhuma etapa de logística cadastrada.'
            : 'Nenhuma coluna encontrada para este fluxo.'}
        </Typography>
      </Box>
    )
  }

  const boardSx = {
    display: 'flex',
    gap: COLUMN_GAP,
    overflowX: 'auto',
    overflowY: 'hidden',
    alignItems: 'stretch',
    height: '100%',
    minHeight: 0,
    pb: 1,
  }

  if (!canDrag) {
    return (
      <Box sx={boardSx}>
        {columns.map((column, index) => {
          const palette = getColumnStyle(index)

          return (
            <Box
              key={column.statusId}
              sx={{
                ...columnSx,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
              }}
            >
              <ColumnHeader column={column} color={palette.color} showTiragem={isSheetFlow} />
              <ColumnBody
                column={column}
                palette={palette}
                activeFlow={activeFlow}
                canDrag={false}
                isMoving={isMoving}
                loadingMoreColumnId={loadingMoreColumnId}
                movingCardId={movingCardId}
                onLoadMoreColumn={onLoadMoreColumn}
                onCardClick={onCardClick}
              />
            </Box>
          )
        })}
      </Box>
    )
  }

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Box sx={boardSx}>
        {columns.map((column, index) => {
          const palette = getColumnStyle(index)

          return (
            <Box
              key={column.statusId}
              sx={{
                ...columnSx,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
              }}
            >
              <ColumnHeader column={column} color={palette.color} showTiragem={isSheetFlow} />
              <StrictModeDroppable
                droppableId={column.statusId}
                isDropDisabled={isMoving}
                ignoreContainerClipping
              >
                {(provided, snapshot) => (
                  <ColumnBody
                    column={column}
                    palette={palette}
                    activeFlow={activeFlow}
                    canDrag
                    isDraggingOver={snapshot.isDraggingOver}
                    isMoving={isMoving}
                    loadingMoreColumnId={loadingMoreColumnId}
                    movingCardId={movingCardId}
                    droppableProvided={provided}
                    onLoadMoreColumn={onLoadMoreColumn}
                    onCardClick={onCardClick}
                  />
                )}
              </StrictModeDroppable>
            </Box>
          )
        })}
      </Box>
    </DragDropContext>
  )
}

export default FlowManagementKanban
